import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Send, Mail, Copy, ExternalLink } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const custKey = (t) => (t.customer_email || t.customer_phone || t.customer_name || "").toString().trim().toLowerCase();

/**
 * "Generate Combined Invoice" — takes the trips a dispatcher ticked on the
 * calendar / board, posts them to /invoices/consolidated/merge (which folds in
 * and voids any unpaid single-trip invoices they already sit on) and then
 * offers one-click SMS / email of the resulting single invoice.
 */
export default function CombineInvoiceModal({ trips, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(null);

  const sorted = [...trips].sort((a, b) => (a.pickup_datetime || "").localeCompare(b.pickup_datetime || ""));
  const total = sorted.reduce((a, t) => a + Number(t.fare || 0), 0);
  const clients = new Set(sorted.map(custKey));
  const mixed = clients.size > 1;
  const alreadyInvoiced = sorted.filter(t => t.consolidated_invoice_number);
  const lead = sorted[0] || {};

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/invoices/consolidated/merge", { trip_ids: sorted.map(t => t.booking_id) });
      setResult(r.data);
      toast.success(`Invoice ${r.data.invoice_number} created · ${money(r.data.total)}`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate invoice");
    } finally { setBusy(false); }
  };

  const send = async (channel) => {
    setSending(channel);
    try {
      await api.post(`/invoices/consolidated/${result.invoice_id}/${channel}`);
      toast.success(channel === "email" ? "Invoice emailed" : "Invoice texted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to send ${channel}`);
    } finally { setSending(null); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.payment_url); toast.success("Payment link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="combine-invoice-modal" className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#050505] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
        <button data-testid="combine-invoice-close" onClick={onClose} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white" aria-label="Close">✕</button>
        <div className="overline mb-1">{result ? "Combined invoice ready" : "Generate combined invoice"}</div>
        <div className="font-serif text-2xl text-[#D4AF37] mb-1">{result?.customer_name || lead.customer_name || lead.customer_email || "Client"}</div>
        <div className="text-xs text-[#A1A1AA] mb-4">{result?.trip_count ?? sorted.length} trip{(result?.trip_count ?? sorted.length) === 1 ? "" : "s"} · one total to pay</div>

        {!result && (
          <>
            <div className="border border-white/10">
              {sorted.map((t, i) => (
                <div key={t.booking_id} data-testid={`combine-trip-${t.booking_id}`} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-white/5 last:border-b-0 text-xs items-center">
                  <div className="col-span-1 text-[#71717A]">{i + 1}</div>
                  <div className="col-span-3">
                    <div className="text-[#F5F2EB]">{t.pickup_datetime ? new Date(t.pickup_datetime).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}</div>
                    <div className="font-mono text-[10px] text-[#D4AF37]">{t.confirmation_code}</div>
                  </div>
                  <div className="col-span-6 text-[#A1A1AA] truncate" title={`${t.pickup_address} → ${t.dropoff_address}`}>
                    {(t.pickup_address || "").split(",")[0]} → {(t.dropoff_address || "").split(",")[0]}
                    {t.consolidated_invoice_number && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-amber-300">on {t.consolidated_invoice_number}</span>}
                  </div>
                  <div className="col-span-2 text-right font-mono text-[#F5F2EB]">{money(t.fare)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-baseline justify-between border-t border-white/10 mt-4 pt-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Grand total</span>
              <span className="font-serif text-3xl text-[#D4AF37]" data-testid="combine-total">{money(total)}</span>
            </div>
            {mixed && <div data-testid="combine-mixed-warning" className="mt-3 text-xs text-red-300 border border-red-400/40 bg-red-500/5 px-3 py-2">These trips belong to different clients — pick trips for one client only.</div>}
            {!mixed && alreadyInvoiced.length > 0 && (
              <div data-testid="combine-fold-notice" className="mt-3 text-xs text-amber-200/90 border border-amber-400/30 bg-amber-500/5 px-3 py-2">
                {alreadyInvoiced.length} trip{alreadyInvoiced.length === 1 ? " is" : "s are"} already on an unpaid invoice — those invoices will be voided and folded into this one.
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="combine-cancel" onClick={onClose} className="btn-ghost !py-2 !px-4 text-[11px]">Cancel</button>
              <button data-testid="combine-generate" onClick={generate} disabled={busy || mixed || sorted.length === 0}
                className="btn-gold !py-2 !px-5 text-[11px] disabled:opacity-40 inline-flex items-center gap-2">
                <FileText size={13}/>{busy ? "Generating…" : "Generate Combined Invoice"}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="combine-result">
            <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 flex items-baseline justify-between">
              <div>
                <div className="font-mono text-[#D4AF37]" data-testid="combine-result-number">{result.invoice_number}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1">
                  {result.trip_count} trips{result.merged_from?.length ? ` · replaced ${result.merged_from.join(", ")}` : ""}
                </div>
              </div>
              <div className="font-serif text-3xl text-[#D4AF37]">{money(result.total)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button data-testid="combine-send-sms" onClick={() => send("sms")} disabled={!!sending} className="btn-gold !py-2.5 text-[11px] inline-flex items-center justify-center gap-2 disabled:opacity-40"><Send size={13}/>{sending === "sms" ? "Texting…" : "Text invoice (SMS)"}</button>
              <button data-testid="combine-send-email" onClick={() => send("email")} disabled={!!sending} className="btn-gold !py-2.5 text-[11px] inline-flex items-center justify-center gap-2 disabled:opacity-40"><Mail size={13}/>{sending === "email" ? "Emailing…" : "Email invoice"}</button>
              <button data-testid="combine-copy-link" onClick={copyLink} className="btn-ghost !py-2.5 text-[11px] inline-flex items-center justify-center gap-2"><Copy size={13}/>Copy pay link</button>
              <a data-testid="combine-open-pdf" href={`${api.defaults.baseURL}/invoices/consolidated/${result.invoice_id}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost !py-2.5 text-[11px] inline-flex items-center justify-center gap-2"><ExternalLink size={13}/>Open PDF</a>
            </div>
            <div className="flex justify-end mt-5">
              <button data-testid="combine-done" onClick={onClose} className="btn-ghost !py-2 !px-4 text-[11px]">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
